
import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Save } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { sessionFormats, currencies } from '@/utils/profile-options';

const DURATION_OPTIONS = [
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1 hour 30 minutes' },
  { value: 120, label: '2 hours' },
];

interface NewServiceFormProps {
  newService: {
    service_name: string;
    description: string;
    session_instructions?: string;
    price: string;
    duration_minutes: number;
    formats_available: string[];
    currency: string;
    is_active: boolean;
    is_free_session: boolean;
    parental_consent_required?: boolean;
    adults_only?: boolean;
  };
  selectedFormats: Record<string, boolean>;
  isSubmitting: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (name: string, value: any) => void;
  onFormatChange: (format: string, checked: boolean) => void;
  onToggleActive: (value: boolean) => void;
  onToggleFreeSession: (value: boolean) => void;
  onToggleParentalConsent?: (value: boolean) => void;
  onToggleAdultsOnly?: (value: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const NewServiceForm = ({
  newService,
  selectedFormats,
  isSubmitting,
  onInputChange,
  onSelectChange,
  onFormatChange,
  onToggleActive,
  onToggleFreeSession,
  onToggleParentalConsent,
  onToggleAdultsOnly,
  onSubmit
}: NewServiceFormProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Service</CardTitle>
        <CardDescription>
          Create a new service to offer to your clients
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Service name */}
            <div className="space-y-2">
              <Label htmlFor="service_name">Service Title<span className="text-red-500">*</span></Label>
              <Input
                id="service_name"
                name="service_name"
                value={newService.service_name}
                onChange={onInputChange}
                placeholder="e.g., Initial Consultation, Full Session, etc."
                className="w-full"
                required
              />
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration_minutes">Duration<span className="text-red-500">*</span></Label>
              <Select 
                value={String(newService.duration_minutes)} 
                onValueChange={(value) => onSelectChange('duration_minutes', Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Price and currency */}
            <div className="space-y-2">
              <Label htmlFor="price">Price<span className="text-red-500">*</span></Label>
              <div className="flex">
                <Select 
                  value={newService.currency} 
                  onValueChange={(value) => onSelectChange('currency', value)}
                >
                  <SelectTrigger className="w-[100px] rounded-r-none border-r-0">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.value} value={currency.value}>
                        {currency.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  value={newService.price}
                  onChange={onInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full rounded-l-none"
                  required
                />
              </div>
            </div>

            {/* Session formats */}
            <div className="space-y-2">
              <Label>Session Format<span className="text-red-500">*</span></Label>
              <div className="flex flex-wrap gap-4 pt-2">
                {sessionFormats.map((format) => (
                  <div key={format.value} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`format-${format.value}`}
                      checked={selectedFormats[format.value] || false}
                      onCheckedChange={(checked) => 
                        onFormatChange(format.value, checked as boolean)
                      }
                    />
                    <label 
                      htmlFor={`format-${format.value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {format.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={newService.description}
              onChange={onInputChange}
              placeholder="Describe what this service includes..."
              className="w-full min-h-[100px]"
            />
          </div>

          {/* Session Instructions (Private) */}
          <div className="space-y-2">
            <Label htmlFor="session_instructions">Session Instructions (Private)</Label>
            <Textarea
              id="session_instructions"
              name="session_instructions"
              value={(newService as any).session_instructions || ''}
              onChange={onInputChange}
              placeholder="Provide details like Zoom link or in-person address. Visible only after booking."
              className="w-full min-h-[100px]"
            />
            <p className="text-sm text-gray-500">Only visible to clients after booking and sent in confirmation email.</p>
          </div>

          {/* Free session toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="is_free_session">Free / Intern Session</Label>
              <Switch 
                id="is_free_session"
                checked={newService.is_free_session}
                onCheckedChange={onToggleFreeSession}
              />
            </div>
            <p className="text-sm text-gray-500">
              {newService.is_free_session ? 'This is marked as a free or intern session' : 'This is a regular paid service'}
            </p>
          </div>

          {/* Service Requirements */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-medium">Service Requirements</h3>
            
            {/* Parental Consent */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="parental_consent_required">Parental Consent Required</Label>
                <Switch 
                  id="parental_consent_required"
                  checked={newService.parental_consent_required || false}
                  onCheckedChange={onToggleParentalConsent}
                />
              </div>
              <p className="text-sm text-gray-500">
                {newService.parental_consent_required ? 'Parental consent will be required for minors' : 'No parental consent requirement'}
              </p>
            </div>

            {/* Adults Only */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="adults_only">Only Accepting Clients Aged 18 and Above</Label>
                <Switch 
                  id="adults_only"
                  checked={newService.adults_only || false}
                  onCheckedChange={onToggleAdultsOnly}
                />
              </div>
              <p className="text-sm text-gray-500">
                {newService.adults_only ? 'Only clients 18+ can book this service' : 'All ages can book this service'}
              </p>
            </div>
          </div>

          {/* Active status */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Service Status</Label>
              <Switch 
                id="is_active"
                checked={newService.is_active}
                onCheckedChange={onToggleActive}
              />
            </div>
            <p className="text-sm text-gray-500">
              {newService.is_active ? 'This service will be available for booking' : 'This service will be hidden from clients'}
            </p>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save New Service
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
